/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  Button,
  OutlinedInput
} from '@mui/material';
import { makeStyles } from '@material-ui/styles';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_WARNING } from 'styles/theme';
import { ZERO_GUID } from 'modules/enumerations/enumerationIds';

const useStyles = makeStyles(() => ({
  card: { width: '100%', flexGrow: 1, flexDirection: 'row', justifyContent: 'space-between' },
  cardContainer: { maxWidth: '100% !important', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' },
  iconRow: { display: 'flex', flexDirection: 'row' },
  mb2: { marginBottom: '4px', marginRight: '16px' }
}));

const theme = createTheme({ palette: { primary: COLOR_PRIMARY, secondary: COLOR_SECONDARY, warning: COLOR_WARNING } });

const RecurringPayableInfoCard = ({
  isReadOnly,
  rpInfo,
  updateRpInfo,
  voucherTypes = [],
  banks = [],

  notPayables = [],
  currencies = []
}) => {
  const classes = useStyles();
  const rpi = rpInfo?.recurringPayableInformation ?? {};
  const banking = rpInfo?.bankingInformation ?? {};
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);

  const [onHoldOpen, setOnHoldOpen] = useState(false);
  const [onHoldReasonDraft, setOnHoldReasonDraft] = useState(rpi.onHoldReason || '');

  const patchRPI = changes =>
    updateRpInfo({
      ...rpInfo,
      recurringPayableInformation: { ...(rpInfo?.recurringPayableInformation ?? {}), ...changes }
    });

  const currencyLabel = id => (currencies.find(c => String(c.id) === String(id)) || {}).code || 'USD';
  const currencyFull = id => (currencies.find(c => String(c.id) === String(id)) || {}).title || '';

  const patchBanking = changes =>
    updateRpInfo({
      ...rpInfo,
      bankingInformation: {
        recurringPayableBankingId: banking?.recurringPayableBankingId ?? null,
        recurringPayableId: rpi?.recurringPayableId ?? banking?.recurringPayableId ?? null,
        bankId: banking?.bankId === '' ? null : banking?.bankId,
        ...(rpInfo?.bankingInformation ?? {}),
        ...changes
      }
    });

  return (
    <ThemeProvider theme={theme}>
      <Card className={classes.card} sx={{ mt: 1 }}>
        <Box>
          <CardContent className={classes.cardContainer}>
            <div className="row justify-space-between align-center">
              <div className={classes.iconRow}>
                <Tooltip title="Recurring Payable Details" placement="right">
                  <FontAwesomeIcon icon={['fas', 'file-contract']} size="2x" />
                </Tooltip>
                <Typography variant="h5" sx={{ pl: '20px', fontSize: '1.3125rem', fontWeight: 'bold' }}>
                  Recurring Payable Details
                </Typography>
              </div>
            </div>
          </CardContent>
          <CardContent sx={{ p: '10px', ml: '10px', pt: 0 }}>
            <Grid container spacing={1} columns={5}>
              <Grid item md={1}>
                <Box>
                  <InputLabel>Invoice Number</InputLabel>
                  <TextField
                    disabled={isReadOnly}
                    value={rpi.invoiceNumber || ''}
                    sx={{ maxWidth: 360 }}
                    onChange={e => patchRPI({ invoiceNumber: e.target.value })}
                  />
                </Box>
              </Grid>
              <Grid item md={1}>
                <Box>
                  <InputLabel>Voucher Type</InputLabel>
                  <Select
                    value={rpi.voucherTypeId || ZERO_GUID}
                    sx={{ maxWidth: 180, minWidth: 180 }}
                    disabled={isReadOnly}
                    onChange={e => patchRPI({ voucherTypeId: e.target.value })}
                  >
                    {(voucherTypes || []).map(opt => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.title}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Grid>
              <Grid item md={1}>
                <Box>
                  <InputLabel>Bank</InputLabel>
                  <Select
                    value={banking?.bankId ?? ''}
                    onChange={e => patchBanking({ bankId: e.target.value })}
                    disabled={isReadOnly}
                    displayEmpty
                    sx={{ maxWidth: '180px', minWidth: '180px' }}
                    input={<OutlinedInput labelWidth={100} />}
                  >
                    <MenuItem value="" disabled>
                      Select a bank
                    </MenuItem>
                    {banks.map(b => (
                      <MenuItem key={b.id} value={b.id}>
                        {b.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Grid>
              <Grid item md={1}>
                <Box>
                  <InputLabel>Amount *</InputLabel>
                  <TextField
                    disabled={isReadOnly}
                    value={rpi.amount ?? 0}
                    sx={{ maxWidth: 360 }}
                    type="number"
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    onChange={e => patchRPI({ amount: e.target.value })}
                  />
                </Box>
              </Grid>

              <Grid item md={1}>
                <Box sx={{ pl: '5px' }}>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Currency
                    </Typography>
                  </div>
                  <div className={classes.mb2}>
                    <Typography
                      variant="body2"
                      sx={{ color: '#41B7C0', fontWeight: 500, fontSize: '1rem', cursor: 'pointer' }}
                      onClick={() => { if(!isReadOnly) setShowCurrencyModal(true); }}
                    >
                      {`${currencyLabel(rpi.receiptCurrencyId)}  ->  ${currencyLabel(rpi.payInCurrencyId)}`}
                    </Typography>
                  </div>
                </Box>
              </Grid>
              <Grid item md={1}>
                <Box>
                  <InputLabel>Not Payable</InputLabel>
                  <Select
                    value={rpi.notPayableId || ZERO_GUID}
                    sx={{ maxWidth: 180, minWidth: 180 }}
                    disabled={isReadOnly}
                    onChange={e => patchRPI({ notPayableId: e.target.value })}
                  >
                    {(notPayables || []).map(opt => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.title}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Grid>
              <Grid item md={1}>
                <Box sx={{ pt: '8px' }}>
                  <FormControl>
                    <FormControlLabel
                      label="On Hold"
                      control={<Checkbox disabled={isReadOnly} checked={!!rpi.onHoldReason} />}
                      onChange={(_, checked) => {
                        if (isReadOnly) return;
                        if (checked) {
                          setOnHoldReasonDraft(rpi.onHoldReason || '');
                          setOnHoldOpen(true);
                        } else {
                          patchRPI({ onHoldReason: '' });
                        }
                      }}
                    />
                  </FormControl>
                </Box>
                <Box sx={{ mr: '16px' }}>
                  <FormControl>
                    <FormControlLabel
                      label="Wire Transfer"
                      control={<Checkbox disabled={isReadOnly} checked={!!rpi.isWireTransfer} />}
                      onChange={(_, checked) => patchRPI({ isWireTransfer: checked })}
                    />
                  </FormControl>
                </Box>
              </Grid>
              <Grid item md={4}>
                <Box>
                  <InputLabel>Description</InputLabel>
                  <TextField
                    disabled={isReadOnly}
                    value={rpi.description || ''}
                    sx={{ minWidth: 360, width: '100%' }}
                    onChange={e => patchRPI({ description: e.target.value })}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
          <Dialog open={showCurrencyModal} fullWidth={false} maxWidth="lg">
            <Box sx={{ p: 3 }}>
              <Typography variant="h5">Currency Selection</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', p: 2 }}>
              <Box sx={{ mr: 2, mt: 2 }}>
                <Typography variant="body2">Receipt Currency</Typography>
              </Box>
              <Box sx={{ mr: 2 }}>
                <Select
                  value={rpi.receiptCurrencyId || ZERO_GUID}
                  sx={{ maxWidth: 180, minWidth: 180 }}
                  disabled={isReadOnly}
                  onChange={e => patchRPI({ receiptCurrencyId: e.target.value })}
                >
                  {currencies.map(opt => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.title} ({opt.code})
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Box sx={{ p: 3, pt: 2 }}>
                <Typography variant="body2">{currencyFull(rpi.receiptCurrencyId)}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', p: 2 }}>
              <Box sx={{ mr: 2, mt: 2 }}>
                <Typography variant="body2">Pay In Currency</Typography>
              </Box>
              <Box sx={{ mr: 2 }}>
                <Select
                  value={rpi.payInCurrencyId || ZERO_GUID}
                  sx={{ maxWidth: 180, minWidth: 180 }}
                  disabled={isReadOnly}
                  onChange={e => patchRPI({ payInCurrencyId: e.target.value })}
                >
                  {currencies.map(opt => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.title} ({opt.code})
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Box sx={{ p: 3, pt: 2 }}>
                <Typography variant="body2">{currencyFull(rpi.payInCurrencyId)}</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'row', p: 2 }}>
              <Box sx={{ mr: 2, mt: 2 }}>
                <Typography variant="body2">
                  {`${currencyLabel(rpi.receiptCurrencyId)}  ->  ${currencyLabel(rpi.payInCurrencyId)}`}
                </Typography>
              </Box>
              <Box sx={{ mr: 2 }}>
                <TextField
                  value={rpi.currencyExchangeRate ?? ''}
                  sx={{ maxWidth: 180 }}
                  type="number"
                  onChange={e => patchRPI({ currencyExchangeRate: e.target.value })}
                />
              </Box>
              <Box sx={{ mr: 2, mt: 2 }}>
                <Typography variant="body2">
                  {`${currencyLabel(rpi.receiptCurrencyId)}  ->  ${currencyLabel(rpi.receiptCurrencyId)}`}
                </Typography>
              </Box>
              <Box sx={{ mr: 2, mt: 2 }}>
                <Typography variant="body2">1.00</Typography>
              </Box>
            </Box>

            <DialogActions sx={{ p: '1.25rem' }}>
              <Button onClick={() => setShowCurrencyModal(false)} sx={{ color: '#41B7C0', '&:hover': { color: '#3d959c' } }}>
                Cancel
              </Button>
              <Button
                onClick={() => setShowCurrencyModal(false)}
                color="primary"
                variant="contained"
                sx={{ backgroundColor: '#41B7C0', '&:hover': { backgroundColor: '#3d959c' } }}
              >
                Set Selection
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog open={onHoldOpen} fullWidth maxWidth="sm" onClose={() => setOnHoldOpen(false)}>
            <Box sx={{ p: 3, pt: 2 }}>
              <Typography>Enter the reason for putting this recurring payable on hold:</Typography>
            </Box>
            <Box sx={{ px: 3 }}>
              <TextField
                value={onHoldReasonDraft}
                onChange={e => setOnHoldReasonDraft(e.target.value)}
                fullWidth
                multiline
                minRows={3}
                placeholder="On hold reason"
              />
            </Box>
            <DialogActions sx={{ p: '1.25rem' }}>
              <Button onClick={() => setOnHoldOpen(false)} sx={{ color: '#41B7C0', '&:hover': { color: '#3d959c' } }}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const reason = String(onHoldReasonDraft || '').trim();
                  if (!reason) {
                    setOnHoldOpen(false);
                    return;
                  }
                  patchRPI({ onHoldReason: reason });
                  setOnHoldOpen(false);
                }}
                color="primary"
                variant="contained"
                disabled={isReadOnly}
                sx={{ backgroundColor: '#41B7C0', '&:hover': { backgroundColor: '#3d959c' } }}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Card>
    </ThemeProvider>
  );
};

export default RecurringPayableInfoCard;
